import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import {
    InfiniteScrollCustomEvent,
    IonButton,
    IonCardContent,
    IonIcon,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    ModalController,
    PopoverController,
} from '@ionic/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { User } from '../../../../user/entities/user';
import { ICardActionButton } from '../../../../common/entities/card-action-button.interface';
import { ButtonConfig } from '../../../../common/entities/modal/modal-button';
import { ModalConfig } from '../../../../common/entities/modal/modal-config';
import { ModalTyp } from '../../../../common/entities/modal/modal-typ';
import { IonicColor } from '../../../../common/entities/toast/ionic-color';
import { LoadingService } from '../../../../common/services/loading/loading.service';
import { ModalAlertService } from '../../../../common/services/modal';
import { ToastService } from '../../../../common/services/toast-service/toast-service.service';
import { LoggingService } from '../../../../logging/logging.service';
import { ExerciseType } from '../../../entities/exerciseSession';
import { Therapy } from '../../../entities/therapy';
import { CopyTemplateModalComponent } from '../../copy-template-modal/copy-template-modal.component';
import { TaskPlanService } from '../task-plan.service';
import { LoadingProcess, LoadingStates } from '../../../../table/components/curafida-table/loading.process';

import { CurafidaPopoverSelectionComponent } from '../../../../table/components/curafida-popover-selection/curafida-popover-selection.component';
import { ActionButton, ActionItemType } from '../../../../table/entities/action-menu.item';
import { ActionType } from '../../../../table/entities/table';
import { TaskPlanTable, TaskPlanTableRow } from '../task-plan.table-config';
import { LazyLoadedList } from '../../../../common/lazy-loaded.list';
import { PaginatedResponse, PaginationParameter } from '../../../../common/entities/paginated-response';
import { CurafidaTableComponent } from '../../../../table/components/curafida-table/curafida-table.component';
import { CurafidaCardComponent } from '../../../../common/components/curafida-card/curafida-card.component';
import { finalize, firstValueFrom } from 'rxjs';
import { CurafidaAuthService } from '../../../../authentication/curafida-auth.service';
import { mayAddTaskPlan } from '../../../../user/services/patient-menu-actions';
import { PatientActionWorkflowService } from '../../../../user/services/patient-action-workflow.service';

@Component({
    selector: 'lib-patient-task-plan-list',
    templateUrl: './patient-task-plan-list.component.html',
    styleUrls: ['./patient-task-plan-list.component.scss'],
    imports: [
        CurafidaTableComponent,
        IonCardContent,
        IonButton,
        TranslatePipe,
        CurafidaCardComponent,
        IonIcon,
        IonInfiniteScroll,
        IonInfiniteScrollContent,
    ],
})
export class PatientTaskPlanListComponent implements OnInit {
    private readonly modalController = inject(ModalController);
    private readonly taskPlanService = inject(TaskPlanService);
    private readonly toastService = inject(ToastService);
    private readonly modalAlertService = inject(ModalAlertService);
    private readonly loadingService = inject(LoadingService);
    private readonly popoverController = inject(PopoverController);
    private readonly patientActionWorkflowService = inject(PatientActionWorkflowService);
    private readonly authService = inject(CurafidaAuthService);
    protected readonly log = inject(LoggingService).getLogger(this.constructor.name);

    readonly user = input<User>();
    readonly hideEditButtons = input(false);
    /** Mutated in place; every change comes with a new loading state, which repaints the view */
    readonly lazyLoadedList = new LazyLoadedList<TaskPlanTable>(20);
    readonly loadingState = signal<LoadingStates>(LoadingProcess.initLoadingProcess());
    /** Whether the logged-in user may create a task plan for the patient */
    readonly canAddTaskPlan = computed(() => {
        const loggedInUser = this.authService.loggedInUser();
        return !!loggedInUser && mayAddTaskPlan(loggedInUser);
    });
    readonly actionButtons = computed<ICardActionButton[]>(() =>
        this.canAddTaskPlan()
            ? [
                  {
                      icon: 'add',
                      id: 'add-supervisor',
                      isDisabled: false,
                      isHidden: false,
                      isIconButton: false,
                      title: 'TASK.PLAN.ADD',
                  },
              ]
            : [],
    );
    private isModalOpened = false;

    ngOnInit() {
        this.initItems(this.lazyLoadedList.initialParameter());
    }

    nextItems(event: InfiniteScrollCustomEvent) {
        this.taskPlanService
            .fetchTables({
                username: this.user().username,
                ...this.lazyLoadedList.nextParameter(),
            })
            .subscribe({
                next: (response) => {
                    const initialPlanTables: TaskPlanTable[] = [];
                    response.items.forEach((it) => {
                        const existing = this.lazyLoadedList.list.items.find((planTable) => it.id === planTable.id);
                        if (existing) {
                            existing.config.list = PaginatedResponse.init([
                                ...existing.config.list.items,
                                ...it.rows.map((row) => new TaskPlanTableRow(row)),
                            ]);
                        } else {
                            initialPlanTables.push(new TaskPlanTable(it, 'TASK.PLAN.ANY_ASSIGN'));
                        }
                    });
                    const clone = {
                        ...response,
                        items: initialPlanTables,
                    };
                    this.lazyLoadedList.append(clone);
                    this.loadingState.set(LoadingProcess.finishedSuccessfullyLoadingProcess());
                },
                error: (message) => {
                    this.log.error('Error in nextItems', message);
                    this.toastService.showToast('TASK.PLAN.FETCH_FAILED', IonicColor.danger);
                    this.loadingState.set(LoadingProcess.finishedWithErrorLoadingProcess());
                    return event.target.complete();
                },
                complete: () => event.target.complete(),
            });
    }

    async addNewPlanTemplate() {
        if (this.isModalOpened) {
            return;
        }
        // The button is hidden without the rights, this keeps the modal closed for any other caller
        if (!this.canAddTaskPlan()) {
            this.toastService.showToast('TASK.PLAN.NO_WRITE_ACCESS', IonicColor.danger);
            return;
        }
        this.isModalOpened = true;
        let taskPlan: Therapy;
        try {
            taskPlan = await this.patientActionWorkflowService.openTaskPlanModal(this.user());
        } finally {
            // Also when the modal failed to open, or adding would stay blocked for good
            this.isModalOpened = false;
        }
        // Undefined when the selection was cancelled, so nothing changed
        if (taskPlan) {
            this.loadingState.set(LoadingProcess.initLoadingProcess());
            this.taskPlanService.fetch(taskPlan.id).subscribe({
                // initItems() settles the loading state once the list is reloaded
                next: () => this.initItems(this.lazyLoadedList.refreshParameter()),
                error: (message) => {
                    this.log.error('Error in addNewPlanTemplate', message);
                    this.toastService.showToast('TASK.PLAN.FETCH_FAILED', IonicColor.danger);
                    this.loadingState.set(LoadingProcess.finishedWithErrorLoadingProcess());
                },
            });
            this.toastService.showToast('ADD_TASK_PLAN_SUCCESS_MESSAGE', IonicColor.success);
        }
    }

    async showRemovePrompt(taskPlan: TaskPlanTable) {
        const title = 'TASK.PLAN.REMOVE';
        const description = 'TASK.PLAN.REMOVE_EXPLANATION';
        const modalConfig = new ModalConfig();
        modalConfig.modalTyp = ModalTyp.INFORMATION;
        modalConfig.title = title;
        modalConfig.titleIcon = 'warning-outline';
        modalConfig.description = description;

        modalConfig.buttonRight = new ButtonConfig();
        modalConfig.buttonRight.buttonText = 'TASK.PLAN.REMOVE';
        modalConfig.buttonRight.buttonColor = 'danger';
        const action = await this.modalAlertService.showModal(modalConfig);
        if (action && action.action === 'right') {
            this.removePlan(taskPlan);
        }
    }

    removePlan(table: TaskPlanTable) {
        this.loadingService.startLoadingModal();
        this.taskPlanService
            .delete(table.id)
            // `complete` never comes after an error, so the loading modal would stay open
            .pipe(finalize(() => this.loadingService.stopLoadingModal()))
            .subscribe({
                next: () => {
                    this.initItems(this.lazyLoadedList.refreshParameter());
                    this.toastService.showToast('TASK.PLAN.REMOVE_SUCCESS_MESSAGE', IonicColor.success);
                },
                error: (message) => {
                    this.log.error('Error in removePlan', message);
                    this.toastService.showToast('TASK.PLAN.REMOVE_ERROR', IonicColor.danger);
                },
            });
    }

    async presentActionButtons(event: MouseEvent, taskPlan: TaskPlanTable) {
        const taskPlanResource = await firstValueFrom(this.taskPlanService.fetch(taskPlan.id));
        const items: ActionButton[] = [];
        items.push(new ActionButton(ActionItemType.BUTTON, 'REMOVE', ActionType.DELETE));
        items.push(new ActionButton(ActionItemType.BUTTON, 'EDIT', ActionType.UPDATE));

        const popover = await this.popoverController.create({
            component: CurafidaPopoverSelectionComponent,
            cssClass: 'my-custom-class',
            translucent: true,
            event: event,
            componentProps: {
                actionItems: items,
            },
        });
        await popover.present();
        const { data } = await popover.onDidDismiss();
        if (data === ActionType.UPDATE) await this.updatePlan(taskPlanResource);
        if (data === ActionType.DELETE) await this.showRemovePrompt(taskPlan);
    }

    async updatePlan(taskPlan: Therapy) {
        const modal = await this.modalController.create({
            component: CopyTemplateModalComponent,
            cssClass: 'full-width-modal',
            componentProps: {
                patient: this.user(),
                exerciseType: ExerciseType.TASK,
                taskPlan: taskPlan,
            },
        });
        await modal.present();
        await modal.onDidDismiss();
    }

    private initItems(parameter: PaginationParameter) {
        return this.taskPlanService
            .fetchTables({
                username: this.user().username,
                ...parameter,
            })
            .subscribe({
                next: (response) => {
                    const clone = {
                        ...response,
                        items: response.items.map((it) => new TaskPlanTable(it, 'TASK.PLAN.ANY_ASSIGN')),
                    };
                    this.lazyLoadedList.init(clone);
                    this.loadingState.set(LoadingProcess.finishedSuccessfullyLoadingProcess());
                },
                error: (message) => {
                    this.log.error('Error in initItems', message);
                    this.toastService.showToast('TASK.PLAN.FETCH_FAILED', IonicColor.danger);
                    this.loadingState.set(LoadingProcess.finishedWithErrorLoadingProcess());
                },
            });
    }
}
